import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t, formatMoney, formatDate } from '../i18n';
import { monthKey as currentMonth } from '../lib/practiceTime';
import { unassignedAmount } from '../lib/payments';
import { Card, Spinner, Alert, EmptyState, Table, Badge, Button } from '../components/ui';

/**
 * Cash flow by month.
 *
 * Received is bucketed by the date the money arrived; owed by the date the
 * session happened — an unpaid March session is March debt even when it is
 * settled in May. Both patient payments and obra social reimbursements are
 * included, grouped at company level.
 *
 * Each professional's own. It used to offer admin the whole practice and a
 * breakdown by professional; what a professional earns from their patients is
 * theirs, like the patients themselves (migration 0038). The practice's own
 * income is subscriptions, which has its own screen.
 */

const monthOf = (value) => String(value).slice(0, 7);

const monthLabel = (key) =>
  formatDate(`${key}-01T12:00:00Z`, { month: 'long', year: 'numeric' });

const CashFlowView = () => {
  const { therapistId } = useSession();
  const [entries, setEntries] = useState([]);
  // Payments that have not covered a turno yet: money in hand all the same.
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [{ data, error: entryError }, { data: creditRows }] = await Promise.all([
      supabase
        .from('cash_flow_entries')
        .select('*')
        .eq('therapist_id', therapistId),
      supabase
        .from('patient_payment_balances')
        .select('amount, sessions_covered, sessions_remaining')
        .eq('therapist_id', therapistId)
        .gt('sessions_remaining', 0),
    ]);
    if (entryError) setError(entryError.message);
    setEntries(data ?? []);
    setCredits(creditRows ?? []);
    setLoading(false);
  }, [therapistId]);

  useEffect(() => { load(); }, [load]);

  /** month -> { received, owed, coverages: { name: { received, owed } } } */
  const byMonth = useMemo(() => {
    const map = new Map();
    for (const entry of entries) {
      if (!entry.month) continue;
      const key = monthOf(entry.month);
      if (!map.has(key)) map.set(key, { received: 0, owed: 0, coverages: new Map() });
      const bucket = map.get(key);
      const amount = Number(entry.amount ?? 0);
      bucket[entry.kind] += amount;

      if (!bucket.coverages.has(entry.coverage)) {
        bucket.coverages.set(entry.coverage, { received: 0, owed: 0 });
      }
      bucket.coverages.get(entry.coverage)[entry.kind] += amount;
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const totals = useMemo(() => entries.reduce((acc, entry) => {
    acc[entry.kind] += Number(entry.amount ?? 0);
    return acc;
  }, { received: 0, owed: 0 }), [entries]);

  // The year is the figure an accountant asks for, and it is the one that has
  // to hold every peso that came in — including what is not against a turno
  // yet, which is why it is named on the card underneath.
  const year = currentMonth().slice(0, 4);
  const thisYear = useMemo(
    () => entries
      .filter((entry) => entry.kind === 'received' && monthOf(entry.month).startsWith(year))
      .reduce((sum, entry) => sum + Number(entry.amount ?? 0), 0),
    [entries, year],
  );
  const unassigned = useMemo(
    () => credits.reduce((sum, credit) => sum + unassignedAmount(credit), 0),
    [credits],
  );

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('cashFlow.title')}</h1>
        <p className="text-sm text-slate-500">{t('cashFlow.subtitle')}</p>
      </div>

      {/* A read that failed shows as a failure with a way to try again. It used
          to sit above $ 0,00 and "Todavía no hay movimientos", which read as
          real figures (seen on a phone, 15 Sep 2026). */}
      {error ? (
        <Alert tone="danger" title={t('cashFlow.loadFailed')}>
          <p>{error}</p>
          <Button variant="secondary" className="mt-3" onClick={load}>
            <RefreshCw size={16} />{t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Card>
              <p className="flex items-center gap-2 text-xs uppercase text-slate-500">
                <TrendingUp size={14} className="text-emerald-600" />
                {t('cashFlow.collectedThisYear', { year })}
              </p>
              <p className="mt-1 text-3xl font-bold text-emerald-700">{formatMoney(thisYear)}</p>
              <p className="mt-1 text-xs text-slate-500">
                {t('cashFlow.unassigned', { amount: formatMoney(unassigned) })}
              </p>
              <p className="text-xs text-slate-400">{t('cashFlow.unassignedHint')}</p>
            </Card>
            <Card>
              <p className="flex items-center gap-2 text-xs uppercase text-slate-500">
                <TrendingUp size={14} className="text-emerald-600" />{t('cashFlow.receivedAll')}
              </p>
              <p className="mt-1 text-3xl font-bold text-emerald-700">{formatMoney(totals.received)}</p>
            </Card>
            <Card>
              <p className="flex items-center gap-2 text-xs uppercase text-slate-500">
                <TrendingDown size={14} className="text-rose-600" />{t('cashFlow.owed')}
              </p>
              <p className="mt-1 text-3xl font-bold text-rose-700">{formatMoney(totals.owed)}</p>
            </Card>
          </div>

          {byMonth.length === 0 ? (
            <Card><EmptyState icon={TrendingUp} title={t('cashFlow.noData')} /></Card>
          ) : (
            <Card title={t('cashFlow.month')}>
              <div className="space-y-6">
                {byMonth.map(([month, bucket]) => (
                  <div key={month}>
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 pb-2">
                      <h3 className="font-semibold text-slate-900 first-letter:uppercase">{monthLabel(month)}</h3>
                      <div className="flex gap-4 text-sm">
                        <span className="text-emerald-700">
                          {t('cashFlow.received')}: <strong>{formatMoney(bucket.received)}</strong>
                        </span>
                        <span className="text-rose-700">
                          {t('cashFlow.owed')}: <strong>{formatMoney(bucket.owed)}</strong>
                        </span>
                      </div>
                    </div>
                    <Table headers={[t('cashFlow.byCoverage'), t('cashFlow.received'), t('cashFlow.owed')]}>
                      {[...bucket.coverages.entries()]
                        .sort((a, b) => (b[1].received + b[1].owed) - (a[1].received + a[1].owed))
                        .map(([coverage, amounts]) => (
                          <tr key={coverage}>
                            <td className="px-3 py-2">
                              <Badge tone={coverage === 'Particular' ? 'neutral' : 'indigo'}>{coverage}</Badge>
                            </td>
                            <td className="px-3 py-2 text-emerald-700">{formatMoney(amounts.received)}</td>
                            <td className="px-3 py-2 text-rose-700">{formatMoney(amounts.owed)}</td>
                          </tr>
                        ))}
                    </Table>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CashFlowView;
